import { ReactNode, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { OrganizationService } from "@common/types/organizationService";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { VStack } from "@design-system/VStack/VStack";
import { PlusIcon } from "@icons";
import { OrganizationServiceFormModal } from "@components/settings/OrganizationServices/OrganizationServiceFormModal";
import { useWorkspacesList } from "@api/workspace.api";
import { useScope } from "@state/scopeContext.lib";
import {
    CloseTemplateFormValues,
    basisOptions,
} from "./closeTemplateForm.constants";

const fields = createFormikFields<CloseTemplateFormValues>();

const CLIENT_FIELD_DESCRIPTION =
    "Selecting a client scopes this template to a single workspace. It will only appear when applying templates to that client, and can only be applied to entities within it.";

const ANY_CLIENT_VALUE = "__any_client__";
const NO_TIER_VALUE = "__no_tier__";

interface CloseTemplateDetailsFieldsProps {
    serviceList: OrganizationService[];
    footer: ReactNode;
    showClientField?: boolean;
}

export const CloseTemplateDetailsFields = ({
    serviceList,
    footer,
    showClientField = false,
}: CloseTemplateDetailsFieldsProps) => {
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const formik = useFormikContext<CloseTemplateFormValues>();
    const { organizationId } = useScope();
    const { data: workspacesData } = useWorkspacesList(
        { organizationId: organizationId ?? undefined },
        { enabled: showClientField },
    );

    const clientItems = useMemo<Array<PickerItemData<string>>>(
        () => [
            { value: ANY_CLIENT_VALUE, label: "Any client" },
            ...(workspacesData ?? []).map((workspace) => ({
                value: workspace.id,
                label: workspace.name,
            })),
        ],
        [workspacesData],
    );

    const tierItems = useMemo<Array<PickerItemData<string>>>(
        () => [
            { value: NO_TIER_VALUE, label: "No tier" },
            ...serviceList.map((service) => ({
                value: service.id,
                label: service.name,
            })),
        ],
        [serviceList],
    );

    // Ids missing from the loaded lists show the placeholder, never a raw id.
    const { workspaceId, serviceId } = formik.values;
    const clientValue =
        workspaceId != null &&
        clientItems.some((item) => item.value === workspaceId)
            ? workspaceId
            : ANY_CLIENT_VALUE;
    let tierValue: string | null = null;
    if (serviceId === null) {
        tierValue = NO_TIER_VALUE;
    } else if (serviceList.some((service) => service.id === serviceId)) {
        tierValue = serviceId;
    }

    return (
        <>
            <VStack gap={16}>
                <fields.Text name="name" label="Name" size="xl" />
                <VStack gap={16}>
                    {showClientField && (
                        <FormikField
                            name="workspaceId"
                            label="Client"
                            control="button"
                            description={CLIENT_FIELD_DESCRIPTION}
                        >
                            {() => (
                                <Picker.Select
                                    searchable
                                    items={clientItems}
                                    value={clientValue}
                                    onValueChange={(id) => {
                                        void formik.setFieldValue(
                                            "workspaceId",
                                            id === ANY_CLIENT_VALUE ? null : id,
                                        );
                                    }}
                                    searchPlaceholder="Search clients..."
                                    emptyLabel="No matching clients"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                        />
                                    }
                                />
                            )}
                        </FormikField>
                    )}
                    <FormikField name="serviceId" label="Tier" control="button">
                        {() => (
                            <Picker.Select
                                searchable
                                items={tierItems}
                                value={tierValue}
                                onValueChange={(nextServiceId) => {
                                    void formik.setFieldValue(
                                        "serviceId",
                                        nextServiceId === NO_TIER_VALUE
                                            ? null
                                            : nextServiceId,
                                    );
                                    void formik.setFieldTouched(
                                        "serviceId",
                                        true,
                                        false,
                                    );
                                }}
                                placeholder="Select tier..."
                                searchPlaceholder="Search tiers..."
                                emptyLabel="No matching tiers"
                                renderFooter={() => (
                                    <Picker.Item
                                        leading={<PlusIcon />}
                                        onSelect={() =>
                                            setIsServiceModalOpen(true)
                                        }
                                    >
                                        Create tier
                                    </Picker.Item>
                                )}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                    />
                                }
                            />
                        )}
                    </FormikField>
                    <fields.Select
                        name="basis"
                        label="Basis"
                        options={basisOptions}
                        clearable
                        emptyValue={null}
                        placeholder="None"
                        buttonProps={{ size: "xl", block: true }}
                    />
                    {footer}
                </VStack>
            </VStack>
            <OrganizationServiceFormModal
                show={isServiceModalOpen}
                onHide={() => setIsServiceModalOpen(false)}
                onCreated={(service) => {
                    void formik.setFieldValue("serviceId", service.id);
                    void formik.setFieldTouched("serviceId", true, false);
                }}
            />
        </>
    );
};
